import { useEffect, useState, type PointerEvent } from 'react';
import { NOME_ESTADO } from '@core/termo/mensagens';
import { tocar } from '../som';

/**
 * Teclado do próprio jogo (§14).
 *
 * Em aparelho de toque o teclado do sistema sobe por cima do tabuleiro e cobre
 * justamente a palavra que está sendo escrita. Este ocupa um lugar fixo no
 * rodapé: o palco encolhe uma vez, a câmera recentraliza uma vez, e nada mais
 * se move enquanto se digita.
 *
 * §4.1 exige diacríticos — sem acento, `coração` não é palavra. Por isso a
 * tecla `ÁÀÂ` troca as letras pelas acentuadas e volta sozinha depois de uma
 * escolha, como o shift de um teclado de celular.
 *
 * O campo real continua sendo o dono do texto e do cursor: as teclas só
 * despacham eventos, e nenhuma delas tira o foco dele.
 *
 * §11: a tecla `Desfazer` mora aqui porque no aparelho de toque não há
 * `Ctrl+Z` — onde o atalho existe, a tecla também tem de existir. A barra
 * superior tem a sua, com o mesmo ícone e a mesma ação; esta continua valendo
 * porque o polegar já está no rodapé e a barra fica a uma tela de distância.
 */

/**
 * As três linhas de cada camada.
 *
 * Com `Desfazer` em cena a última linha fica com onze teclas contra dez das
 * outras, e em 320 px (§14) todas estreitam junto — daí o `min-width: 0` no
 * CSS.
 */
const LINHAS_LETRAS = ['QWERTYUIOP', 'ASDFGHJKLÇ', 'ZXCVBNM-'];
const LINHAS_ACENTOS = ['ÁÀÂÃÉÊ', 'ÍÓÔÕÚÜ', 'Ç-'];

/**
 * O teclado do jogo aparece onde o do sistema atrapalharia: aparelhos cujo
 * apontador principal é o dedo. Não é largura de tela — um notebook em janela
 * estreita tem teclado físico e não precisa deste.
 */
const CONSULTA_TOQUE = '(pointer: coarse)';

export function useTecladoVirtual(): boolean {
  const [toque, setToque] = useState(() => window.matchMedia?.(CONSULTA_TOQUE).matches ?? false);

  useEffect(() => {
    const consulta = window.matchMedia?.(CONSULTA_TOQUE);
    if (consulta === undefined) return;
    const ouvir = () => setToque(consulta.matches);
    ouvir();
    consulta.addEventListener('change', ouvir);
    return () => consulta.removeEventListener('change', ouvir);
  }, []);

  return toque;
}

/** Nome legível de uma tecla, para quem navega por leitor de tela. */
function rotularTecla(caractere: string): string {
  return caractere === '-' ? 'hífen' : `letra ${caractere}`;
}

/* Tocar numa tecla não pode tirar o foco do campo real: é ele que guarda o
   cursor, o rótulo e a composição de acentuadas. `preventDefault` no
   `pointerdown` impede o foco sem impedir o clique. */
function semRoubarFoco(e: PointerEvent) {
  e.preventDefault();
}

export function TecladoVirtual({
  aoLetra,
  aoApagar,
  aoDesfazer,
  podeDesfazer,
  aoEnviar,
  estadoDasLetras,
  desabilitado,
}: {
  aoLetra: (letra: string) => void;
  aoApagar: () => void;
  /** §11: a mesma ação de `Ctrl+Z`, para quem não tem teclado físico. */
  aoDesfazer: () => void;
  /**
   * Nada a desfazer deixa a tecla inerte, em vez de mentir que há.
   *
   * Vem separado de `desabilitado` porque as duas travas são de coisas
   * diferentes: `desabilitado` fecha a **escrita**, e a escrita está fechada
   * justamente na escolha da âncora — que é quando mais se quer desfazer uma
   * ponte mal escolhida. `Ctrl+Z` funciona ali, e a tecla também tem de
   * funcionar.
   */
  podeDesfazer: boolean;
  /** Só a Trama envia: no jogo em cadeia não há palpite a confirmar. */
  aoEnviar?: () => void;
  /**
   * O que já se sabe de cada letra (§23.4). A tecla carrega o melhor estado
   * observado, e é assim que o teclado vira a memória da partida.
   */
  estadoDasLetras?: ReadonlyMap<string, 'certa' | 'presente' | 'ausente'>;
  desabilitado: boolean;
}) {
  const [acentos, setAcentos] = useState(false);
  const linhas = acentos ? LINHAS_ACENTOS : LINHAS_LETRAS;

  const digitar = (letra: string) => {
    aoLetra(letra);
    setAcentos(false);
  };

  return (
    <div className="teclado" role="group" aria-label="Teclado do jogo">
      {linhas.map((linha, indiceLinha) => {
        const ultima = indiceLinha === linhas.length - 1;
        return (
          <div key={linha} className="teclado__linha">
            {ultima && (
              <button
                type="button"
                className="teclado__tecla teclado__tecla--acao"
                onClick={() => {
                  // O ouvinte de cliques ignora o teclado do jogo, senão cada
                  // letra soaria duas vezes. Esta tecla é a exceção: não mexe
                  // no draft, então o som dela não vem de lugar nenhum.
                  tocar('clique');
                  setAcentos((antes) => !antes);
                }}
                onPointerDown={semRoubarFoco}
                aria-pressed={acentos}
                aria-label={acentos ? 'Voltar às letras sem acento' : 'Letras acentuadas'}
              >
                {acentos ? 'ABC' : 'ÁÀÂ'}
              </button>
            )}

            {[...linha].map((caractere) => {
              const sabido = estadoDasLetras?.get(caractere);
              return (
                <button
                  key={caractere}
                  type="button"
                  className="teclado__tecla"
                  data-sabido={sabido}
                  onClick={() => digitar(caractere)}
                  onPointerDown={semRoubarFoco}
                  disabled={desabilitado}
                  // §13: o estado da tecla não pode depender só de cor, então
                  // ele entra no rótulo que o leitor de tela anuncia.
                  aria-label={
                    sabido === undefined
                      ? rotularTecla(caractere)
                      : `${rotularTecla(caractere)}, ${NOME_ESTADO[sabido]}`
                  }
                >
                  {caractere}
                </button>
              );
            })}

            {ultima && (
              <button
                type="button"
                className="teclado__tecla teclado__tecla--acao"
                onClick={aoDesfazer}
                onPointerDown={semRoubarFoco}
                disabled={!podeDesfazer}
                aria-label="Desfazer"
                title="Desfazer"
              >
                <IconeDesfazer />
              </button>
            )}

            {ultima && (
              <button
                type="button"
                className="teclado__tecla teclado__tecla--acao"
                onClick={aoApagar}
                onPointerDown={semRoubarFoco}
                disabled={desabilitado}
                aria-label="Apagar a última letra"
              >
                <span aria-hidden="true">⌫</span>
              </button>
            )}

            {ultima && aoEnviar !== undefined && (
              <button
                type="button"
                className="teclado__tecla teclado__tecla--acao teclado__tecla--enviar"
                onClick={aoEnviar}
                onPointerDown={semRoubarFoco}
                disabled={desabilitado}
              >
                Enviar
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Seta que volta sobre si mesma, com a ponta à esquerda.
 *
 * Um arco aberto, e não o círculo fechado de `Recomeçar` na barra: as duas
 * ações andam para trás, mas uma desfaz um passo e a outra apaga tudo, e
 * confundi-las no toque sairia caro.
 */
export function IconeDesfazer({ tamanho = 20 }: { tamanho?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} aria-hidden="true" focusable="false">
      <path
        d="M9 7.5h5.25a5.25 5.25 0 0 1 0 10.5H8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M11.75 4.5 8.75 7.5l3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
